import { Link, useParams } from 'react-router-dom';

import { useGetCaseIssues } from '@/api/queries/useGetCaseThemes';
import { Icon } from '@/new/components/icon';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/new/components/shadcn/accordion';
import { Button } from '@/new/components/shadcn/button';
import { Skeleton } from '@/new/components/shadcn/skeleton';
import { TextWithReferences } from '@/new/components/text-references';
import { cn } from '@/new/lib/utils';
import { Summary } from '@/types';

const IssuesOverviewLoader = () => {
  return (
    <div className="flex flex-col">
      {[...Array(3)].map((_, index) => (
        <div key={index} className={cn('flex items-center gap-4 py-2.5')}>
          <Skeleton className="h-4 w-4" />
          <Skeleton className="h-4 w-64" />
        </div>
      ))}
    </div>
  );
};

interface IssuesOverviewProps {
  issues: Summary[];
  isLoading?: boolean;
  matterId?: string;
  caseId?: string;
  hasSummary?: boolean;
}

export const IssuesOverview = ({
  issues,
  isLoading = false,
  matterId: propMatterId,
  caseId: propCaseId,
  hasSummary = false,
}: IssuesOverviewProps) => {
  const params = useParams();
  const matterId = propMatterId ?? params.matterId;
  const subsetId = propCaseId ?? params.subsetId;

  if (!matterId || !subsetId) return null;

  const { data: issuesWithCounts = [] } = useGetCaseIssues({ matterId, subsetId });

  const issueCountMap = issuesWithCounts.reduce<Record<string, { count: number; id: string }>>((acc, issue) => {
    acc[issue.name] = { count: issue.count, id: issue.id };
    return acc;
  }, {});

  if (!isLoading && !issues.length) {
    return null;
  }

  return (
    <Accordion
      type="single"
      collapsible
      defaultValue="themes"
      className={cn(
        'w-full border border-gray-200 bg-white duration-150 hover:border-gray-300',
        hasSummary ? 'rounded-b border-t-0' : 'rounded-t rounded-b',
      )}
    >
      <AccordionItem value="themes">
        <AccordionTrigger className="rounded-md border-b bg-gray-50 px-4 py-2 data-[state=closed]:border-b-transparent data-[state=open]:rounded-b-none data-[state=open]:border-gray-200">
          <div className="flex items-center gap-3">
            <Icon className="lucide--lightbulb h-4 w-4 text-gray-500" />
            <span className="font-medium text-gray-700">Issues</span>
          </div>
        </AccordionTrigger>
        <AccordionContent className="px-0">
          {isLoading ? (
            <div className="px-4 py-2">
              <IssuesOverviewLoader />
            </div>
          ) : (
            <Accordion type="single" collapsible className="flex flex-col">
              {issues.map((issue, index) => {
                const issueData = issueCountMap[issue.name];
                const factCount = issueData?.count ?? 0;
                return (
                  <AccordionItem key={index} value={`theme-${index}`}>
                    <AccordionTrigger
                      className={`flex items-center justify-between border-gray-100 px-4 py-2 ${index === issues.length - 1 ? '' : 'border-b'}`}
                    >
                      <div className="flex flex-1 items-center gap-3">
                        <Icon className="lucide--lightbulb text-sky-500" />
                        <div className="flex min-w-0 flex-1 flex-col text-left text-sm font-medium text-gray-700">
                          {issue.name}
                        </div>
                      </div>

                      {factCount > 0 && issueData && (
                        <Button asChild size="sm" className="mr-2">
                          <Link
                            to={`/matters/${matterId}/subsets/${subsetId}/facts?${new URLSearchParams({ issueIds: issueData.id }).toString()}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <span>
                              Explore <b>{factCount}</b> Related Facts
                            </span>
                            <Icon className="lucide--arrow-right h-3 w-3" />
                          </Link>
                        </Button>
                      )}
                    </AccordionTrigger>
                    <AccordionContent className="p-2 px-5">
                      <div className="mt-2 flex flex-col gap-3 leading-relaxed">
                        <TextWithReferences content={issue.content} />
                      </div>

                      {issue.subquestions && issue.subquestions.length > 0 && (
                        <>
                          <div className="my-4 mb-3 flex items-center gap-2 font-semibold text-gray-700">
                            <Icon className="lucide--circle-question-mark h-3 w-3 shrink-0" />
                            Related Questions
                          </div>

                          <Accordion type="single" className="mb-2 flex flex-col" collapsible>
                            {issue.subquestions.map((subquestion, subIndex) => (
                              <AccordionItem
                                key={subIndex}
                                value={`subquestion-${index}-${subIndex}`}
                                className="border-b border-gray-100"
                              >
                                <AccordionTrigger className="py-2 text-left text-sm font-medium text-gray-800 hover:text-blue-500">
                                  {subquestion.question}
                                </AccordionTrigger>
                                <AccordionContent className="pt-2 pb-4 text-sm leading-relaxed text-gray-700">
                                  <TextWithReferences content={subquestion.answer} />
                                </AccordionContent>
                              </AccordionItem>
                            ))}
                          </Accordion>
                        </>
                      )}
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          )}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};
